import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { List, Ticket, UserProfile } from '@/lib/types'
import { cn, formatAiDate, formatDate, formatRelative, formatScheduled, groupMedia, isTicketUrgent, labelColor, PRIORITY_META, priorityLabel } from '@/lib/utils'
import { htmlToText, sanitizeRichHtml } from '@/lib/richtext'
import { Avatar } from './Avatar'
import { AvatarStack } from './AvatarStack'
import { SubscriberPicker } from './SubscriberPicker'
import { InlineAudio, type AudioSpeed } from './InlineAudio'
import { TicketCardMenu } from './TicketCardMenu'
import { MediaPreviewChip } from './MediaPreviewChip'
import {
  Paperclip,
  FileText,
  MessagesSquare,
  MessageCircleMore,
  Star,
  Play,
  Loader2,
  Sparkles,
  HelpCircle,
  CalendarDays,
  Flame,
  ArrowRightLeft,
} from 'lucide-react'

interface Props {
  ticket: Ticket
  onClick?: () => void
  /** Open the fullscreen viewer with the given media URLs. */
  onViewMedia?: (urls: string[]) => void
  /** Open the compact audio panel (with résumés / transcriptions) for this card. */
  onViewAudio?: (urls: string[], ticket: Ticket) => void
  /** Transcribe a single audio URL of this ticket and append the result
   *  to the ticket description. Optional — the inline audio player hides
   *  its "Transcrire" menu entry when not provided. Implementation lives
   *  in App.tsx (it has the messages context needed to find the source
   *  message and invoke the transcribe-audio edge function). */
  onTranscribeAudio?: (
    ticket: Ticket,
    audioUrl: string,
  ) => void | Promise<void>
  /** True while this card's audio transcription is in flight (kicked off
   *  from the InlineAudio kebab « Transcrire » entry). Renders a small
   *  pulsing chip with a spinner so the user sees that the AI is working
   *  — the edge function can take 5–15s and otherwise nothing changes on
   *  screen until the title/description suddenly update. */
  isTranscribing?: boolean
  /** Global audio playback rate + setter, propagated to the InlineAudio
   *  kebab so the user's speed choice applies everywhere. */
  audioSpeed?: AudioSpeed
  onAudioSpeedChange?: (rate: AudioSpeed) => void
  /** Report hover enter (with the card's rect) / leave (null). */
  onHoverChange?: (ticket: Ticket | null, rect: DOMRect | null) => void
  /** Archive this card (reversible — moves it to the archives). */
  onArchive?: (id: string) => void
  /** Permanently delete this card (kebab « Supprimer », undo-toast deferred). */
  onDelete?: (id: string) => void
  /** Manually clear the « new » flag (mark seen) from the card menu. */
  onMarkSeen?: (id: string) => void
  /** Open the cross-project « Move card » dialog. The TicketCardMenu
   *  surfaces this as its « Déplacer la carte » entry. */
  onMove?: () => void
  /** True when this card lives in the owner's central Inbox. Surfaces a
   *  prominent « Affecter à un canal » button so triaging an inbox card
   *  isn't buried in the kebab menu. */
  isInbox?: boolean
  /** Open the « Répondre » dialog (AI-suggested replies + composer).
   *  The TicketCardMenu surfaces this as its « Répondre… » entry. */
  onReply?: () => void
  /** Patch this card in place — used by the contextual menu (priority, labels…). */
  onUpdate?: (id: string, patch: Partial<Ticket>) => void | Promise<void>
  /** All lists of the project — feeds the menu's « move to list » picker. */
  allLists?: List[]
  /** User ids who can be subscribed (owner + project members). */
  subscribableUserIds?: string[]
  /** Current subscribers of this ticket — null = unknown / not loaded. */
  subscriberIds?: Set<string> | null
  /** The viewer — used to highlight "you" in the picker. */
  currentUserId?: string
  /** Subscribe / unsubscribe a user (optimistic + email notify). */
  onToggleSubscriber?: (
    ticketId: string,
    userId: string,
    subscribe: boolean,
  ) => void | Promise<void>
  /** Set when rendered inside DragOverlay (no sortable wrapper, full opacity). */
  dragging?: boolean
  /** True when THIS card is the one being dragged AND the drag has
   *  crossed into a different list. The card collapses to h-0 so the
   *  "ghost" visually moves to the destination column's placeholder
   *  instead of staying behind as a translucent shape in the source. */
  hideAsCrossListSource?: boolean
  /** True while a merge is "charging" — the user has dwelled ~2s over this
   *  card with another one. The card vibrates and grows a yellow halo until
   *  the confirmation modal opens (~4s). Applies to both the dragged overlay
   *  and the static target card. */
  merging?: boolean
  /** Slim one-line layout: title + small avatar + label dots only. Global
   *  setting driven by the user's compact-cards preference. */
  compact?: boolean
  /** True when this card is a live search match — gets a highlight ring. */
  highlighted?: boolean
  /** True while the card is fading out (archived from the board). */
  exiting?: boolean
  /** True for ~320ms right after the card was inserted (locally or via
   *  realtime) — plays a one-shot slide-in animation. */
  entering?: boolean
  /** True when this card was added since the user's previous visit. */
  isNew?: boolean
  /** Profile of the user this card is assigned to. */
  assigneeProfile?: UserProfile
  /** Full profile map — needed by the contextual menu to render the
   *  subscriber picker (display names + avatars). */
  profilesById?: Record<string, UserProfile>
  /** Deduplicated labels used anywhere in the project — surfaced in the
   *  card menu as one-click suggestions for reapplying an existing label
   *  without retyping it. */
  projectLabels?: string[]
}

export function TicketCard({
  ticket,
  onClick,
  onViewMedia,
  onViewAudio,
  onTranscribeAudio,
  isTranscribing = false,
  audioSpeed,
  onAudioSpeedChange,
  onHoverChange,
  onArchive,
  onDelete,
  onMarkSeen,
  onMove,
  isInbox = false,
  onReply,
  onUpdate,
  allLists,
  subscribableUserIds,
  subscriberIds,
  currentUserId,
  onToggleSubscriber,
  dragging = false,
  hideAsCrossListSource = false,
  merging = false,
  compact = false,
  highlighted = false,
  exiting = false,
  entering = false,
  isNew = false,
  assigneeProfile,
  profilesById,
  projectLabels,
}: Props) {
  const { t } = useTranslation()
  const sortable = useSortable({
    id: ticket.id,
    disabled: dragging,
    // listId is read by KanbanBoard's collision detection to tell
    // same-list drags (where we want the native sortable to take over
    // when the cursor is in column padding) apart from cross-list drags
    // (where the column itself is a valid drop target — needed so the
    // user can drop into an empty list or above the first card).
    data: { type: 'ticket', listId: ticket.list_id },
    // Soft + slightly longer than dnd-kit's default (200ms / linear-ish):
    // 320ms with the Material "decelerate" curve so cards glide into
    // their new slot rather than snapping — much more organic when other
    // cards shuffle to make room for the dragged one.
    transition: {
      duration: 320,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
    },
  })
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable

  const media = groupMedia(ticket.media_urls)
  const messageCount = ticket.source_message_ids.length
  // The contact spoke last and we still owe a reply — drives the messaging
  // icon's filled + bouncing state (and the card's emerald ring).
  const awaitingReply = ticket.awaiting_reply === true

  // When the contextual menu is open we lift the card above the blurred
  // backdrop (z-index 50). The popover sits even higher at z-index 70.
  const [menuOpen, setMenuOpen] = useState(false)

  // When the user opens / closes the contextual menu, dismiss any hover
  // preview attached to this card AND start a short cooldown so the preview
  // can't sneak back in on the cursor's path back over the card. Without
  // this, exiting the popover would reveal the hover-preview tooltip the
  // user explicitly chose to leave behind.
  const menuCooldownUntilRef = useRef(0)
  useEffect(() => {
    onHoverChange?.(null, null)
    menuCooldownUntilRef.current = Date.now() + 500
  }, [menuOpen, onHoverChange])

  // Smoothly animate the card's height when it switches between the
  // detailed and compact (one-line) layouts — instead of snapping. We
  // remember the last height, and on a `compact` change we briefly pin the
  // old height then transition to the new one (classic auto-height tween).
  const cardElRef = useRef<HTMLDivElement | null>(null)
  const lastHeightRef = useRef<number | null>(null)
  const setRootRef = (node: HTMLDivElement | null) => {
    cardElRef.current = node
    if (!dragging) setNodeRef(node)
  }
  useLayoutEffect(() => {
    const el = cardElRef.current
    if (!el || dragging) return
    const newH = el.offsetHeight
    const oldH = lastHeightRef.current
    lastHeightRef.current = newH
    if (oldH == null || oldH === newH) return
    el.style.height = `${oldH}px`
    el.style.overflow = 'hidden'
    // Force a reflow so the browser registers the start height before we
    // transition to the target.
    el.getBoundingClientRect()
    el.style.transition = 'height 280ms cubic-bezier(0.4, 0, 0.2, 1)'
    el.style.height = `${newH}px`
    const done = (e: TransitionEvent) => {
      if (e.propertyName !== 'height') return
      el.style.height = ''
      el.style.overflow = ''
      el.style.transition = ''
      el.removeEventListener('transitionend', done)
    }
    el.addEventListener('transitionend', done)
    return () => el.removeEventListener('transitionend', done)
  }, [compact, dragging])

  // Star bounce — counter incremented on every favorite toggle. The icon's
  // `key` is bumped so React re-mounts it (restarts the keyframe even on
  // rapid taps). The animation class is only added once the user has
  // toggled at least once, so the star doesn't bounce on initial render.
  const [starBumps, setStarBumps] = useState(0)

  function toggleFavorite() {
    if (!onUpdate) return
    onUpdate(ticket.id, { favorite: !ticket.favorite })
    setStarBumps((n) => n + 1)
  }

  const style = dragging
    ? undefined
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        // Lift this card above the backdrop while the menu is open so it
        // stays sharp (the backdrop blurs everything behind it).
        zIndex: merging ? 40 : menuOpen ? 60 : undefined,
      }

  /** A clickable media-type chip — opens the lightbox, never triggers drag/open. */
  function MediaChip({
    icon,
    urls,
    label,
  }: {
    icon: React.ReactNode
    urls: string[]
    label: string
  }) {
    if (urls.length === 0) return null
    return (
      <button
        type="button"
        aria-label={label}
        title={label}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          onViewMedia?.(urls)
        }}
        className="inline-flex items-center gap-0.5 rounded-md px-1 py-0.5 text-[var(--color-link)] transition-colors hover:bg-[var(--color-accent-bg)]"
      >
        {icon}
        {urls.length > 1 && urls.length}
      </button>
    )
  }

  return (
    <div
      ref={dragging ? undefined : setRootRef}
      // Tagged so KanbanBoard can identify this card's DOM node via
      // querySelector when computing the live cross-list insertion slot.
      data-ticket-id={ticket.id}
      style={style}
      {...(dragging ? {} : attributes)}
      {...(dragging ? {} : listeners)}
      onClick={onClick}
      onMouseEnter={(e) => {
        // Don't show the preview while the contextual menu is open, and
        // also skip during the cooldown right after a menu open/close so
        // the cursor's path back over the card can't re-pop it.
        if (dragging || isDragging || menuOpen) return
        if (Date.now() < menuCooldownUntilRef.current) return
        onHoverChange?.(ticket, e.currentTarget.getBoundingClientRect())
      }}
      onMouseLeave={() => onHoverChange?.(null, null)}
      className={cn(
        'group relative rounded-2xl border text-left select-none',
        compact ? 'px-3 py-1.5' : 'px-4 py-3',
        // New cards get a soft accent tint + accent border to stand out.
        isNew
          ? 'border-[var(--color-accent)]/40 bg-[var(--color-accent-bg)]'
          : 'border-[var(--color-border-soft)] bg-[var(--color-surface)]',
        exiting
          ? 'pointer-events-none scale-95 opacity-0 transition-all duration-200 ease-in'
          : dragging
            ? 'rotate-1 cursor-grabbing shadow-2xl ring-2 ring-[var(--color-accent)]/30'
            : 'cursor-pointer transition-all duration-150 ease-out hover:-translate-y-px hover:shadow-[var(--shadow-card-hover)]',
        // Resting shadow: tinted with the priority colour if the card has
        // one (visual rank cue at a glance), neutral default otherwise.
        !dragging &&
          !exiting &&
          !isDragging &&
          (ticket.priority
            ? PRIORITY_META[ticket.priority].shadow
            : 'shadow-[var(--shadow-card)]'),
        // While this card is the one being dragged, two visual modes:
        //  - same-list drag (cursor still in source) → keep the slot as
        //    a translucent ghost (Trello). Signals the origin + lets the
        //    user release back there to cancel.
        //  - cross-list drag (cursor moved to another list) → collapse
        //    entirely so the "ghost" visually follows the cursor to the
        //    destination column, where a placeholder makes room.
        !dragging &&
          isDragging &&
          !hideAsCrossListSource &&
          'pointer-events-none opacity-40',
        !dragging &&
          isDragging &&
          hideAsCrossListSource &&
          'pointer-events-none !h-0 !min-h-0 !overflow-hidden !border-0 !p-0 opacity-0',
        !dragging &&
          !exiting &&
          highlighted &&
          'border-[var(--color-accent)] ring-1 ring-inset ring-[var(--color-accent)]',
        // Ongoing exchange: a real conversation (more than one message)
        // where the contact spoke last and we still owe a reply → emerald
        // ring to draw the eye to actionable conversations.
        !dragging &&
          !exiting &&
          !highlighted &&
          awaitingReply &&
          messageCount > 1 &&
          'ring-1 ring-inset ring-emerald-400/70',
        // One-shot entrance animation (slide-in from above + brief accent
        // ring). Driven by App.tsx tracking ~320ms after insertion.
        entering && !dragging && !exiting && 'animate-ticket-in',
        // Merge charging: BOTH the dragged overlay and the hovered target
        // vibrate + grow a yellow halo (starts at 2s, modal opens at 4s).
        merging &&
          !exiting &&
          'animate-merge-charging border-yellow-400 ring-2 ring-yellow-400 relative z-30',
      )}
    >
      {/* Contextual menu (priority / labels / archive) — hover affordance,
          top-right corner. Replaces the previous single-purpose archive
          button so the user can edit the most common card-level fields
          without opening the detail modal. */}
      {!dragging && onArchive && onUpdate && (
        <TicketCardMenu
          ticket={ticket}
          lists={allLists}
          onUpdate={onUpdate}
          onArchive={onArchive}
          onDelete={onDelete}
          isNew={isNew}
          onMarkSeen={onMarkSeen}
          onMove={onMove}
          onReply={onReply}
          onOpenChange={setMenuOpen}
          subscribableUserIds={subscribableUserIds ?? []}
          subscriberIds={subscriberIds ?? null}
          profilesById={profilesById ?? {}}
          currentUserId={currentUserId ?? ''}
          onToggleSubscriber={onToggleSubscriber}
          compact={compact}
          projectLabels={projectLabels}
        />
      )}

      {/* ── Compact mode: single-line layout (title + dots + avatar). ─────────
          Everything else (priority/labels chips, description, audio player,
          message count, media chips, date) is hidden — the contextual menu
          stays accessible via the hover-only `…` for quick edits. */}
      {compact ? (
        <div className="flex items-center gap-2 pr-7">
          {onUpdate && (
            <button
              type="button"
              aria-label={ticket.favorite ? t('card.favoriteRemove') : t('card.favoriteAdd')}
              title={ticket.favorite ? t('card.favoriteRemove') : t('card.favoriteAdd')}
              aria-pressed={ticket.favorite}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                toggleFavorite()
              }}
              className={cn(
                '-ml-0.5 inline-flex shrink-0 items-center justify-center rounded p-0.5 transition-colors',
                ticket.favorite
                  ? 'text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]'
                  : 'text-[var(--color-text-dim)]/40 hover:text-[var(--color-accent)]',
              )}
            >
              <Star
                key={starBumps}
                className={cn('h-3 w-3', starBumps > 0 && 'animate-star-bounce')}
                fill={ticket.favorite ? 'currentColor' : 'none'}
                strokeWidth={ticket.favorite ? 1.5 : 2}
              />
            </button>
          )}
          {isTranscribing && (
            // Span wrapper because lucide-react icons don't accept the
            // native `title` attribute directly (they spread their props
            // onto the SVG, which TypeScript types as forbidding `title`).
            <span title={t('card.transcribingAria')} className="inline-flex shrink-0">
              <Loader2 className="h-3 w-3 animate-spin text-[var(--color-accent)]" />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium leading-snug text-[var(--color-text-strong)]">
            {ticket.title}
          </span>
          {/* Inline media previews — one-tap access to audio / image / video
              without having to expand the card or open the modal. */}
          {media.audio.length > 0 && (
            <button
              type="button"
              aria-label={t('card.viewAudio')}
              title={t('card.viewAudio')}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onViewAudio?.(media.audio, ticket)
              }}
              className="inline-flex shrink-0 items-center justify-center rounded-md p-0.5 text-[var(--color-link)] transition-colors hover:bg-[var(--color-accent-bg)]"
            >
              <Play className="h-3 w-3" fill="currentColor" />
            </button>
          )}
          <MediaPreviewChip
            kind="image"
            urls={media.image}
            label={t('card.viewImages')}
            countTitle={t('card.viewImagesCount', { count: media.image.length })}
            onView={() => onViewMedia?.(media.image)}
            size="sm"
          />
          <MediaPreviewChip
            kind="video"
            urls={media.video}
            label={t('card.viewVideos')}
            countTitle={t('card.viewVideosCount', { count: media.video.length })}
            onView={() => onViewMedia?.(media.video)}
            size="sm"
          />
          <CommentChip
            count={ticket.comments_count ?? 0}
            lastBody={ticket.last_comment_body ?? null}
            lastAt={ticket.last_comment_at ?? null}
            countTitle={t('card.commentsTooltip', {
              count: ticket.comments_count ?? 0,
            })}
          />
          {ticket.priority && (
            <span
              title={priorityLabel(ticket.priority)}
              className={cn(
                'h-2 w-2 shrink-0 rounded-full',
                PRIORITY_META[ticket.priority].dot,
              )}
            />
          )}
          {ticket.labels.slice(0, 4).map((label) => (
            <span
              key={label}
              title={label}
              className={cn(
                'h-2 w-2 shrink-0 rounded-full ring-1 ring-inset',
                labelColor(label),
              )}
            />
          ))}
          {ticket.assignee_id && (
            <Avatar
              profile={assigneeProfile}
              userId={ticket.assignee_id}
              className="h-4 w-4 shrink-0"
              textClassName="text-[7px]"
            />
          )}
        </div>
      ) : (
        <>
      {/* Priority + labels chips + AI-transcribing indicator — rendered
          only when there's something to show. The favorite star lives in
          the footer (next to the date) so this row never appears just
          for it.

          The « Transcription IA » chip pulses + carries a spinner so the
          user sees the AI is working: the transcribe-audio edge function
          takes a few seconds and the title/description rewrite happens
          at the end (atomic), without this chip the card would just
          sit still until it suddenly changes. */}
      {(isTranscribing || ticket.priority || ticket.labels.length > 0) && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1 pr-6">
          {isTranscribing && (
            <span
              title={t('card.transcribingAria')}
              className="inline-flex animate-pulse items-center gap-1 rounded-md bg-[var(--color-accent-bg)] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-[var(--color-accent)] ring-1 ring-inset ring-[var(--color-accent)]/40"
            >
              <Loader2 className="h-2.5 w-2.5 animate-spin" />
              {t('card.transcribingChip')}
            </span>
          )}
          {ticket.priority && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide ring-1 ring-inset',
                PRIORITY_META[ticket.priority].chip,
              )}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  PRIORITY_META[ticket.priority].dot,
                )}
              />
              {priorityLabel(ticket.priority)}
            </span>
          )}
          {ticket.labels.map((label) => (
            <span
              key={label}
              className={cn(
                'rounded-md px-1.5 py-0.5 text-[9.5px] font-medium ring-1 ring-inset',
                labelColor(label),
              )}
            >
              {label}
            </span>
          ))}
        </div>
      )}

      <div className="pr-6 text-[13.5px] font-medium leading-snug text-[var(--color-text-strong)]">
        {ticket.title}
      </div>
      {ticket.description && (
        <div
          className="rich-text mt-1.5 line-clamp-2 text-[12.5px] leading-relaxed text-[var(--color-text)]"
          dangerouslySetInnerHTML={{
            __html: sanitizeRichHtml(ticket.description),
          }}
        />
      )}

      {/* Inline audio player: WhatsApp voice messages are core context,
          the user should hear them without opening any modal. The custom
          InlineAudio component renders a slim player + navigation across
          ALL audios on this card + a kebab menu for transcription. The
          old "+N audio" chip in the footer is therefore retired (we no
          longer route extras through the modal). */}
      {media.audio.length > 0 && (() => {
        const clipsByUrl = new Map(
          (ticket.audio_clips ?? [])
            .filter((c) => c.audio_url)
            .map((c) => [c.audio_url!, c]),
        )
        const transcribedUrls = new Set(ticket.transcribed_audio_urls ?? [])
        return (
          <div className="mt-2">
            <InlineAudio
              urls={media.audio}
              clipsByUrl={clipsByUrl}
              transcribedUrls={transcribedUrls}
              onTranscribe={
                onTranscribeAudio
                  ? (url) => onTranscribeAudio(ticket, url)
                  : undefined
              }
              playbackRate={audioSpeed}
              onPlaybackRateChange={onAudioSpeedChange}
            />
          </div>
        )
      })()}

      {/* Manual « Transcrire la vidéo » trigger — same Whisper + Claude
          path as the audio kebab on InlineAudio. Shown when the card has
          at least one video that hasn't been transcribed yet, and the
          parent wired the handler. Clicking flips the same transcribing
          chip state as a vocal so the user gets a unified feedback. */}
      {onTranscribeAudio &&
        media.video.length > 0 &&
        (() => {
          const transcribed = new Set(ticket.transcribed_audio_urls ?? [])
          const next = media.video.find((u) => !transcribed.has(u))
          if (!next) return null
          return (
            <div className="mt-2">
              <button
                type="button"
                disabled={isTranscribing}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation()
                  onTranscribeAudio(ticket, next)
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-accent-bg)] hover:text-[var(--color-accent-text)] disabled:opacity-60"
                title={t('card.transcribeVideo')}
              >
                {isTranscribing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                {isTranscribing
                  ? t('card.transcribingChip')
                  : t('card.transcribeVideo')}
              </button>
            </div>
          )
        })()}

      {/* Inbox triage: prominent « Affecter à un canal » — the primary
          action for a card sitting in the central inbox, surfaced here so
          it isn't hidden in the kebab menu. */}
      {isInbox && onMove && (
        <div className="mt-2">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onMove()
            }}
            title={t('card.assignChannel')}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[var(--color-accent)]/40 bg-[var(--color-accent-bg)] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--color-accent-text)] transition-colors hover:bg-[var(--color-accent)] hover:text-white"
          >
            <ArrowRightLeft className="h-3.5 w-3.5" />
            {t('card.assignChannel')}
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[11px] text-[var(--color-text-dim)]">
        <span className="inline-flex items-center gap-1.5">
          {onUpdate && (
            <button
              type="button"
              aria-label={ticket.favorite ? t('card.favoriteRemove') : t('card.favoriteAdd')}
              title={ticket.favorite ? t('card.favoriteRemove') : t('card.favoriteAdd')}
              aria-pressed={ticket.favorite}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                toggleFavorite()
              }}
              className={cn(
                '-ml-0.5 inline-flex items-center justify-center rounded-md p-0.5 transition-colors',
                ticket.favorite
                  ? 'text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]'
                  : 'text-[var(--color-text-dim)]/40 hover:text-[var(--color-accent)]',
              )}
            >
              {/* `key` bumps on every toggle → React re-mounts the icon so the
                  bounce animation re-fires even on rapid clicks. The class is
                  only added once the user has toggled (starBumps > 0). */}
              <Star
                key={starBumps}
                className={cn('h-3.5 w-3.5', starBumps > 0 && 'animate-star-bounce')}
                fill={ticket.favorite ? 'currentColor' : 'none'}
                strokeWidth={ticket.favorite ? 1.5 : 2}
              />
            </button>
          )}
          {/* AI-derived signals — surfaced as small icons after the favourite
              star so they sit in the "card metadata" gutter. All bounce
              once when the card is new to this user (isNew → driven by
              lastSeenAt), drawing attention to actionable items without
              becoming noise on subsequent visits.
              Order: urgency first (most demanding of attention), then
              question, then date. */}
          {isTicketUrgent(ticket) && (
            <span
              title={t('card.aiUrgentTooltip')}
              aria-label={t('card.aiUrgentAria')}
              className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-md p-0.5 text-rose-500',
                isNew && 'animate-icon-bounce',
              )}
            >
              <Flame className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
          )}
          {ticket.ai_is_question && (
            <span
              title={t('card.aiQuestionTooltip')}
              aria-label={t('card.aiQuestionAria')}
              className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-md p-0.5 text-amber-500',
                isNew && 'animate-icon-bounce',
              )}
            >
              <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
          )}
          {/* User-set schedule takes precedence over the advisory AI date.
              For PERIODS (range spanning different days), the text "May 22 →
              Jul 26, 2026" eats the whole row and overflows the card — fall
              back to a compact icon-only chip and carry the full period in
              the tooltip. Single dates stay textual. */}
          {ticket.scheduled_start
            ? (() => {
                const fullText = formatScheduled(
                  ticket.scheduled_start,
                  ticket.scheduled_end,
                  ticket.scheduled_all_day,
                )
                const isPeriod =
                  !!ticket.scheduled_end &&
                  new Date(ticket.scheduled_start).toDateString() !==
                    new Date(ticket.scheduled_end).toDateString()
                if (isPeriod) {
                  return (
                    <span
                      className="inline-flex shrink-0 items-center justify-center rounded-md bg-violet-50 p-0.5 text-violet-700 ring-1 ring-inset ring-violet-200"
                      title={fullText}
                      aria-label={fullText}
                    >
                      <CalendarDays className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </span>
                  )
                }
                return (
                  <span
                    className="inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 ring-1 ring-inset ring-violet-200"
                    title={fullText}
                  >
                    <CalendarDays className="h-3 w-3" strokeWidth={2.5} />
                    {fullText}
                  </span>
                )
              })()
            : ticket.ai_mentioned_date ? (
            <button
              type="button"
              title={t('card.aiDateTooltip', { date: formatAiDate(ticket.ai_mentioned_date) })}
              aria-label={t('card.aiDateTooltip', { date: formatAiDate(ticket.ai_mentioned_date) })}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                // Surfaces the date via the title tooltip on hover. Future:
                // open a small popover with an "Ajouter à mon agenda"
                // shortcut (.ics or Google Calendar deep-link).
              }}
              className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-md p-0.5 text-sky-500 transition-colors hover:bg-[var(--color-accent-bg)]',
                isNew && 'animate-icon-bounce',
              )}
            >
              <CalendarDays className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          ) : null}
          {isNew && (
            <span className="rounded-full bg-[var(--color-accent)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
              {t('card.new')}
            </span>
          )}
          {formatDate(ticket.source_date ?? ticket.created_at)}
          {/* Messaging indicator — shown on real conversations (more than
              one message) OR whenever we still owe a reply, even on a
              single-message card. A lone, already-answered card shows
              nothing. The awaiting-reply state always surfaces so the
              « to reply » filter never hides its own signal. */}
          {(messageCount > 1 || awaitingReply) && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5',
                awaitingReply ? 'text-emerald-600' : 'text-emerald-500',
              )}
              title={
                awaitingReply
                  ? t('card.awaitingReplyTooltip')
                  : t('card.messageSourcesTooltip', { count: messageCount })
              }
            >
              <MessagesSquare
                className={cn(
                  'h-3.5 w-3.5',
                  // Awaiting our reply → filled icon + a discreet bounce
                  // every 5s; once we answer it reverts to a green outline.
                  awaitingReply && 'fill-emerald-500/90 animate-icon-bounce-loop',
                )}
              />
              {messageCount > 1 && messageCount}
            </span>
          )}
          <CommentChip
            count={ticket.comments_count ?? 0}
            lastBody={ticket.last_comment_body ?? null}
            lastAt={ticket.last_comment_at ?? null}
            countTitle={t('card.commentsTooltip', {
              count: ticket.comments_count ?? 0,
            })}
          />
        </span>
        <div className="flex items-center gap-1.5">
          <div className="-mr-0.5 flex items-center gap-0.5">
            <MediaPreviewChip
              kind="image"
              urls={media.image}
              label={t('card.viewImages')}
              countTitle={t('card.viewImagesCount', { count: media.image.length })}
              onView={() => onViewMedia?.(media.image)}
              size="md"
            />
            <MediaPreviewChip
              kind="video"
              urls={media.video}
              label={t('card.viewVideos')}
              countTitle={t('card.viewVideosCount', { count: media.video.length })}
              onView={() => onViewMedia?.(media.video)}
              size="md"
            />
            {/* Audio extras no longer get a footer chip — the InlineAudio
                player above carries its own prev/next navigation across
                every audio attached to the card. */}
            <MediaChip
              icon={<FileText className="h-3.5 w-3.5" />}
              urls={media.pdf}
              label={t('card.viewPdfs')}
            />
            <MediaChip
              icon={<Paperclip className="h-3.5 w-3.5" />}
              urls={media.file}
              label={t('card.viewFiles')}
            />
          </div>
          {(() => {
            // Union: assignee + subscribers. The stack collapses to a
            // single avatar for a 1-person card (= the assignee alone),
            // and grows into the overlapping pile only when the card is
            // genuinely shared. The assignee gets an accent ring to
            // distinguish "responsible" from "watcher".
            //
            // When the parent provides the full picker plumbing
            // (subscribable list + toggle handler), we render the
            // interactive SubscriberPicker — click opens a popover with
            // checkboxes to add/remove members on the fly without
            // entering the detail modal. Otherwise we fall back to the
            // pure-display AvatarStack.
            const members: string[] = []
            if (ticket.assignee_id) members.push(ticket.assignee_id)
            if (subscriberIds) {
              for (const id of subscriberIds) {
                if (!members.includes(id)) members.push(id)
              }
            }
            if (members.length === 0) return null
            if (onToggleSubscriber && subscribableUserIds) {
              return (
                <SubscriberPicker
                  ticketId={ticket.id}
                  subscribableUserIds={subscribableUserIds}
                  subscriberIds={subscriberIds ?? null}
                  assigneeId={ticket.assignee_id ?? null}
                  currentUserId={currentUserId}
                  profilesById={profilesById ?? {}}
                  onToggle={onToggleSubscriber}
                />
              )
            }
            return (
              <AvatarStack
                userIds={members}
                profilesById={profilesById ?? {}}
                assigneeId={ticket.assignee_id ?? null}
                max={5}
                size="md"
              />
            )
          })()}
        </div>
      </div>
        </>
      )}
    </div>
  )
}

/**
 * Internal-comment chip — distinct from the messaging icon. The messaging
 * chip is the multi-bubble MessagesSquare (a whole conversation); the
 * comment chip is a single chat bubble with three dots inside
 * (MessageCircleMore) so it visually reads as « note / commentaire » while
 * staying in the same speech-bubble family. Tinted amber to set it apart
 * from the green conversation chip. The badge shows the count. On hover,
 * a small tooltip shows the LATEST comment body (with relative timestamp)
 * so the user can see at a glance what was said without opening the card.
 */
function CommentChip({
  count,
  lastBody,
  lastAt,
  countTitle,
}: {
  count: number
  lastBody?: string | null
  lastAt?: string | null
  countTitle: string
}) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const timerRef = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    },
    [],
  )
  if (count <= 0) return null

  const previewText = htmlToText(lastBody ?? '').slice(0, 220)

  function enter(e: React.MouseEvent<HTMLSpanElement>) {
    if (!previewText) return
    const r = e.currentTarget.getBoundingClientRect()
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => setRect(r), 350)
  }
  function leave() {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setRect(null)
  }

  const POPOVER_HALF_WIDTH = 160
  const flipBelow = rect ? rect.top < 200 : false
  const preview =
    rect && previewText ? (
      <div
        className="pointer-events-none fixed z-[80] w-[320px] rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-2.5 shadow-[var(--shadow-modal)]"
        style={{
          left: Math.min(
            window.innerWidth - POPOVER_HALF_WIDTH * 2 - 8,
            Math.max(8, rect.left + rect.width / 2 - POPOVER_HALF_WIDTH),
          ),
          ...(flipBelow
            ? { top: rect.bottom + 8 }
            : { top: Math.max(8, rect.top - 8), transform: 'translateY(-100%)' }),
        }}
      >
        <div className="line-clamp-6 whitespace-pre-wrap text-[12px] leading-relaxed text-[var(--color-text)]">
          {previewText}
        </div>
        {lastAt && (
          <div className="mt-1.5 text-[10.5px] text-[var(--color-text-dim)]">
            {formatRelative(lastAt)}
          </div>
        )}
      </div>
    ) : null

  return (
    <>
      <span
        title={countTitle}
        onMouseEnter={enter}
        onMouseLeave={leave}
        className="inline-flex shrink-0 items-center gap-0.5 text-sky-500"
      >
        <MessageCircleMore className="h-3.5 w-3.5" />
        {count}
      </span>
      {preview && createPortal(preview, document.body)}
    </>
  )
}
